import React from "react";

// Curved bottom edge (used under the purple hero) - white curve on colored bg
export const CurveBottom = ({ color = "#ffffff", flip = false, className = "" }) => (
  <svg
    className={`absolute left-0 w-full ${flip ? "top-0 -mt-px" : "bottom-0 -mb-px"} ${className}`}
    viewBox="0 0 1440 80"
    preserveAspectRatio="none"
    style={{ height: 60, transform: flip ? "scaleY(-1)" : undefined }}
  >
    <path d="M0,80 L0,40 C240,90 480,0 720,30 C960,60 1200,70 1440,20 L1440,80 Z" fill={color} />
  </svg>
);

// Wavy top edge for a colored section (color = section color) over white
export const WaveTop = ({ color = "#6dcdf2" }) => (
  <svg className="block w-full" viewBox="0 0 1440 70" preserveAspectRatio="none" style={{ height: 60 }}>
    <path d="M0,30 C360,-10 720,60 1080,20 C1260,0 1380,10 1440,40 L1440,70 L0,70 Z" fill={color} />
  </svg>
);

// Scalloped bottom edge hanging from colored section onto white
export const ScallopDivider = ({ color = "#63c79a" }) => (
  <svg className="block w-full" viewBox="0 0 1440 120" preserveAspectRatio="none" style={{ height: 100 }}>
    <path
      d="M0,0 L1440,0 L1440,20 C1380,110 1260,110 1200,20 C1140,110 1020,110 960,20 C900,110 780,110 720,20 C660,110 540,110 480,20 C420,110 300,110 240,20 C180,110 60,110 0,20 Z"
      fill={color}
    />
  </svg>
);

export const Cloud = ({ className = "", face = false, size = 200 }) => (
  <svg className={className} width={size} height={size * 0.6} viewBox="0 0 200 120" aria-hidden="true">
    <ellipse cx="60" cy="80" rx="50" ry="30" fill="#fff" />
    <ellipse cx="140" cy="80" rx="50" ry="30" fill="#fff" />
    <circle cx="80" cy="55" r="35" fill="#fff" />
    <circle cx="125" cy="50" r="38" fill="#fff" />
    {face && (
      <g>
        <circle cx="88" cy="62" r="9" fill="#6dcdf2" />
        <circle cx="118" cy="62" r="9" fill="#6dcdf2" />
        <circle cx="90" cy="63" r="4" fill="#1f1f1f" />
        <circle cx="120" cy="63" r="4" fill="#1f1f1f" />
        <path d="M95 80 Q103 88 111 80" stroke="#1f1f1f" strokeWidth="3" fill="none" strokeLinecap="round" />
      </g>
    )}
  </svg>
);

export const Sun = ({ className = "", size = 220 }) => (
  <svg className={`${className} sun-spin`} width={size} height={size} viewBox="0 0 200 200" aria-hidden="true">
    {Array.from({ length: 12 }).map((_, i) => (
      <polygon key={i} points="100,4 88,40 112,40" fill="#f7931e" transform={`rotate(${i * 30} 100 100)`} />
    ))}
    <circle cx="100" cy="100" r="58" fill="#f3c932" />
    <circle cx="82" cy="92" r="9" fill="#fff" />
    <circle cx="118" cy="92" r="9" fill="#fff" />
    <circle cx="84" cy="93" r="4.5" fill="#5a2a1a" />
    <circle cx="120" cy="93" r="4.5" fill="#5a2a1a" />
    <circle cx="70" cy="110" r="6" fill="#f7931e" opacity="0.7" />
    <circle cx="130" cy="110" r="6" fill="#f7931e" opacity="0.7" />
    <path d="M88 114 Q100 124 112 114" stroke="#5a2a1a" strokeWidth="3.5" fill="none" strokeLinecap="round" />
  </svg>
);

// Bamboo stalk cluster (replaces the cactus/plants of the original hero)
export const Bamboo = ({ className = "", height = 420, flip = false }) => (
  <svg
    className={className}
    width={height * 0.42}
    height={height}
    viewBox="0 0 170 400"
    aria-hidden="true"
    style={{ transform: flip ? "scaleX(-1)" : undefined }}
  >
    <rect x="20" y="0" width="26" height="400" rx="8" fill="#63c79a" />
    <rect x="70" y="60" width="22" height="340" rx="8" fill="#4fb98a" />
    {[60, 130, 200, 270, 340].map((y) => (
      <rect key={y} x="16" y={y} width="34" height="10" rx="4" fill="#2e8b57" />
    ))}
    {[120, 200, 280, 360].map((y) => (
      <rect key={y} x="67" y={y} width="28" height="9" rx="4" fill="#2e8b57" />
    ))}
    {[[46, 90, 20], [46, 240, -10], [92, 160, 30], [92, 300, 15]].map(([x, y, r], i) => (
      <g key={i} transform={`translate(${x} ${y}) rotate(${r})`}>
        <line x1="0" y1="0" x2="45" y2="-20" stroke="#2e8b57" strokeWidth="3" />
        <ellipse cx="52" cy="-26" rx="26" ry="8" fill="#8fdcb8" transform="rotate(-25 52 -26)" />
        <ellipse cx="40" cy="-6" rx="22" ry="7" fill="#63c79a" transform="rotate(10 40 -6)" />
      </g>
    ))}
  </svg>
);

// Curved text heading using SVG textPath (like "Adventures in learning!")
export const ArcText = ({ text, color = "#0d3d2b", className = "" }) => (
  <svg className={`w-full max-w-3xl mx-auto ${className}`} viewBox="0 0 800 200" aria-label={text} role="img">
    <defs>
      <path id="arcPath" d="M 40 150 Q 400 -40 760 150" fill="none" />
    </defs>
    <text fill={color} fontFamily="Literata, serif" fontWeight="800" fontSize="58">
      <textPath href="#arcPath" startOffset="50%" textAnchor="middle">
        {text}
      </textPath>
    </text>
  </svg>
);

export const Coin = ({ className = "", size = 60 }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 60 60" aria-hidden="true">
    <circle cx="30" cy="30" r="28" fill="#f3c932" stroke="#c9a01c" strokeWidth="3" />
    <circle cx="30" cy="30" r="20" fill="none" stroke="#c9a01c" strokeWidth="2" />
    <text x="30" y="39" textAnchor="middle" fontSize="26" fontWeight="800" fill="#8a6d00" fontFamily="Work Sans, sans-serif">$</text>
  </svg>
);
